"use client";

import { Volume, Volume2Icon, VolumeX } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { If } from "react-haiku";

const INLINE_VIDEO_SOUND_KEY = "thinknado:inlineVideoSound";

let globalSoundEnabled = false;
let globalSoundInitialized = false;
const globalSoundListeners = new Set<(value: boolean) => void>();

function initGlobalSound() {
  if (globalSoundInitialized) return;
  globalSoundInitialized = true;
  if (typeof window === "undefined") return;

  try {
    globalSoundEnabled = window.localStorage.getItem(INLINE_VIDEO_SOUND_KEY) === "1";
  } catch {
    globalSoundEnabled = false;
  }
}

function setGlobalSoundEnabled(value: boolean) {
  globalSoundEnabled = value;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(INLINE_VIDEO_SOUND_KEY, value ? "1" : "0");
    } catch {
      // ignore
    }
  }
  globalSoundListeners.forEach((listener) => listener(value));
}

export const InlineVideo = ({
  src = "",
  className = "",
  hasSound = false,
  size= 'h-full w-full',
  ...props
}) => {
  const observerRef = useRef<HTMLVideoElement>(null);
  const [sound, setSound] = useState(false);

  useEffect(() => {
    initGlobalSound();
    setSound(globalSoundEnabled);

    const listener = (value: boolean) => setSound(value);
    globalSoundListeners.add(listener);
    return () => {
      globalSoundListeners.delete(listener);
    };
  }, []);

  const toggleSound = useCallback(() => {
    if (!hasSound) return;
    const next = !sound;
    setGlobalSoundEnabled(next);
    if (next) {
      observerRef.current?.play();
    }
  }, [hasSound, sound]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!observerRef.current) return;
          if (entry.isIntersecting) {
            observerRef.current?.play();
          } else {
            observerRef.current?.pause();
          }
        });
      },
      { threshold: 0.15 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, []);

  if (sound) {
    return (
      <div className={`relative bg-black ${size}`}>
        <video
          ref={observerRef}
          className={className}
          autoPlay
          playsInline
          loop
          src={src}
          {...props}
          onClick={toggleSound}
        />
        <If isTrue={hasSound}>
          <button
            className="absolute bottom-4 right-4 z-10 bg-purple rounded-full p-4"
            onClick={toggleSound}
          >
            <If isTrue={sound}>
              <VolumeX color="white" />
            </If>
            <If isTrue={!sound}>
              <Volume2Icon color="white" />
            </If>
          </button>
        </If>
      </div>
    );
  }

  return (
    <div className={`relative ${size}`}>
      <video
        ref={observerRef}
        className={className}
        autoPlay
        loop
        muted
        playsInline
        src={src}
        {...props}
        onClick={toggleSound}
      />
      <If isTrue={hasSound}>
        <button
          className="absolute bottom-4 right-4 z-10 bg-purple rounded-full p-4"
          onClick={toggleSound}
        >
          <If isTrue={sound}>
            <VolumeX color="white" />
          </If>
          <If isTrue={!sound}>
            <Volume2Icon color="white" />
          </If>
        </button>
      </If>
    </div>
  );
};
